import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import { splitSearchTokens } from '@/shared/domain/searchTokens'
import { findClientIdsBySearch, findLeadIdsBySearch } from '@/infrastructure/supabase/partySearch'
import type { QuoteRepository } from '../../application/QuoteRepository'
import type {
  QuoteItem,
  QuoteListFilters,
  QuoteRecord,
  QuoteStats,
  QuoteStatus,
  QuoteTaxLine,
  QuoteTaxSnapshot,
} from '../../domain/quote'
import { legacyQuoteRowSchema, mapLegacyQuote } from './legacyQuoteMapper'

const quoteColumns =
  'id,numero,organization_id,lead_id,client_id,montant,taxes,total,statut,expiration,notes,version,created_at,updated_at,deleted_at,lead:leads(numero,prenom,nom,telephone,courriel,adresse,ville,code_postal),client:clients(numero,prenom,nom,entreprise,telephone,courriel,adresse,ville,province,code_postal),competiteur,contract:contracts!contracts_source_quote_id_fkey(id,numero)'

const legacyStatus: Record<QuoteStatus, string> = {
  DRAFT: 'brouillon',
  READY: 'prete',
  SENT: 'envoyee',
  ACCEPTED: 'acceptee',
  REJECTED: 'refusee',
  EXPIRED: 'expiree',
}

const createDraftResultSchema = z.object({
  quoteId: z.string(),
  number: z.string(),
  status: z.literal('DRAFT'),
  version: z.number().int().positive(),
  createdAt: z.string(),
})

const replaceItemsResultSchema = z.object({
  quoteId: z.uuid(),
  number: z.string(),
  version: z.number().int().positive(),
  subtotalCents: z.number().int().nonnegative(),
  taxTotalCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  taxProfileVersion: z.number().int().positive().nullable(),
})

const prepareQuoteResultSchema = z.object({
  quoteId: z.uuid(),
  number: z.string(),
  status: z.literal('READY'),
  version: z.number().int().positive(),
  preparedAt: z.string(),
})

const sendQuoteResultSchema = z.object({
  deliveryId: z.uuid(),
  quoteId: z.uuid(),
  number: z.string(),
  status: z.literal('SENT'),
  version: z.number().int().positive(),
  documentId: z.uuid(),
  providerMessageId: z.string().min(1),
  sentAt: z.string(),
})

const acceptQuoteResultSchema = z.object({
  quoteId: z.uuid(),
  number: z.string(),
  status: z.literal('ACCEPTED'),
  version: z.number().int().positive(),
  acceptedAt: z.string(),
  documentId: z.uuid(),
  recordedAt: z.string(),
})

const quoteItemRowSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  quantity_milli: z.coerce.number().int().positive(),
  unit_price_cents: z.coerce.number().int().nonnegative(),
  subtotal_cents: z.coerce.number().int().nonnegative(),
  taxable: z.boolean(),
  display_order: z.number().int().positive(),
})

const quoteTaxSnapshotRowSchema = z.object({
  profile_version: z.number().int().positive(),
  profile_label: z.string(),
  rounding_strategy: z.enum(['PER_TAX_TOTAL_HALF_UP', 'PER_ITEM_HALF_UP']),
  calculated_at: z.string(),
})

const quoteTaxLineRowSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  label: z.string(),
  rate_ppm: z.number().int().nonnegative(),
  calculation_base: z.enum(['SUBTOTAL', 'RUNNING_TOTAL']),
  taxable_amount_cents: z.coerce.number().int().nonnegative(),
  tax_amount_cents: z.coerce.number().int().nonnegative(),
  display_order: z.number().int().positive(),
})

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
  count: number | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

function sanitizeSearch(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

const quoteStatsRowSchema = z.object({
  total: z.number(),
  draft: z.number(),
  ready: z.number(),
  sent: z.number(),
  accepted: z.number(),
  rejected: z.number(),
  expired: z.number(),
})

/** Les compteurs en une requête (`quote_list_stats_v1`), mêmes règles que les filtres de la liste. */
async function loadStats(client: AppSupabaseClient): Promise<QuoteStats> {
  const rpcClient = client as unknown as SupabaseClient
  const response = asResponse(await rpcClient.rpc('quote_list_stats_v1'))
  if (response.error) throw new Error(`QUOTE_COUNT_FAILED:${response.error.code ?? 'UNKNOWN'}`)
  const row: unknown = Array.isArray(response.data) ? response.data[0] : response.data
  const parsed = quoteStatsRowSchema.safeParse(row)
  if (!parsed.success) throw new Error('QUOTE_COUNT_RESPONSE_INVALID')
  return parsed.data
}

function mapRows(data: unknown): { quotes: QuoteRecord[]; anomalies: number } {
  if (!Array.isArray(data)) throw new Error('QUOTE_LIST_RESPONSE_INVALID')
  const quotes: QuoteRecord[] = []
  let anomalies = 0
  for (const candidate of data) {
    const parsed = legacyQuoteRowSchema.safeParse(candidate)
    if (!parsed.success) {
      anomalies += 1
      reportUnexpectedError(parsed.error, { feature: 'quotes', operation: 'map-list-row' })
      continue
    }
    try {
      quotes.push(mapLegacyQuote(parsed.data))
      if (!parsed.data.client && !parsed.data.lead) anomalies += 1
    } catch (error) {
      anomalies += 1
      reportUnexpectedError(error, { feature: 'quotes', operation: 'map-list-row' })
    }
  }
  return { quotes, anomalies }
}

async function loadQuoteComposition(
  client: SupabaseClient,
  quoteId: string,
): Promise<{ items: QuoteItem[]; taxSnapshot?: QuoteTaxSnapshot }> {
  const [itemsRaw, snapshotRaw, taxLinesRaw] = await Promise.all([
    client
      .from('quote_items')
      .select('id,description,quantity_milli,unit_price_cents,subtotal_cents,taxable,display_order')
      .eq('quote_id', quoteId)
      .order('display_order'),
    client
      .from('quote_tax_snapshots')
      .select('profile_version,profile_label,rounding_strategy,calculated_at')
      .eq('quote_id', quoteId)
      .maybeSingle(),
    client
      .from('quote_tax_lines')
      .select('id,code,label,rate_ppm,calculation_base,taxable_amount_cents,tax_amount_cents,display_order')
      .eq('quote_id', quoteId)
      .order('display_order'),
  ])
  const itemResponse = asResponse(itemsRaw)
  const snapshotResponse = asResponse(snapshotRaw)
  const taxLineResponse = asResponse(taxLinesRaw)
  if (itemResponse.error || snapshotResponse.error || taxLineResponse.error)
    throw new Error('QUOTE_COMPOSITION_FAILED')
  const items = z.array(quoteItemRowSchema).safeParse(itemResponse.data)
  const snapshot = quoteTaxSnapshotRowSchema.nullable().safeParse(snapshotResponse.data)
  const taxLines = z.array(quoteTaxLineRowSchema).safeParse(taxLineResponse.data)
  if (!items.success || !snapshot.success || !taxLines.success)
    throw new Error('QUOTE_COMPOSITION_RESPONSE_INVALID')
  const mappedItems = items.data.map((item): QuoteItem => ({
    id: item.id,
    description: item.description,
    quantityMilli: item.quantity_milli,
    unitPriceCents: item.unit_price_cents,
    subtotalCents: item.subtotal_cents,
    taxable: item.taxable,
    displayOrder: item.display_order,
  }))
  if (!snapshot.data) return { items: mappedItems }
  const lines = taxLines.data.map((line): QuoteTaxLine => ({
    id: line.id,
    code: line.code,
    label: line.label,
    ratePpm: line.rate_ppm,
    calculationBase: line.calculation_base,
    taxableAmountCents: line.taxable_amount_cents,
    taxAmountCents: line.tax_amount_cents,
    displayOrder: line.display_order,
  }))
  return {
    items: mappedItems,
    taxSnapshot: {
      profileVersion: snapshot.data.profile_version,
      profileLabel: snapshot.data.profile_label,
      roundingStrategy: snapshot.data.rounding_strategy,
      calculatedAt: snapshot.data.calculated_at,
      lines,
    },
  }
}

export function createSupabaseQuoteRepository(client: AppSupabaseClient): QuoteRepository {
  return {
    async list(filters: QuoteListFilters) {
      const from = (filters.page - 1) * filters.pageSize
      const to = from + filters.pageSize - 1
      let query = client
        .from('quotes')
        .select(quoteColumns, { count: 'exact' })
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .range(from, to)
      if (filters.status) query = query.eq('statut', legacyStatus[filters.status])
      const search = filters.search ? sanitizeSearch(filters.search) : ''
      if (search) {
        // La Soumission ne porte pas le nom de la personne : on retrouve
        // d'abord les Clients et Leads qui correspondent.
        const tokens = splitSearchTokens(search)
        const [byClient, byLead] = await Promise.all([
          findClientIdsBySearch(client as unknown as SupabaseClient, tokens),
          findLeadIdsBySearch(client as unknown as SupabaseClient, tokens),
        ])
        const clauses = [
          `numero.ilike.%${search}%`,
          `notes.ilike.%${search}%`,
          ...(byClient.length > 0 ? [`client_id.in.(${byClient.join(',')})`] : []),
          ...(byLead.length > 0 ? [`lead_id.in.(${byLead.join(',')})`] : []),
        ]
        query = query.or(clauses.join(','))
      }
      const [rawResponse, stats] = await Promise.all([query, loadStats(client)])
      const response = asResponse(rawResponse)
      if (response.error) throw new Error(`QUOTE_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const mapped = mapRows(response.data)
      return {
        items: mapped.quotes,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? mapped.quotes.length,
        stats,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: mapped.anomalies },
      }
    },
    async getByReference(reference) {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        reference,
      )
      let query = client.from('quotes').select(quoteColumns).is('deleted_at', null)
      query = uuid ? query.eq('id', reference) : query.eq('numero', reference)
      const response = asResponse(await query.maybeSingle())
      if (response.error) throw new Error(`QUOTE_DETAIL_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (response.data === null) return null
      const parsed = legacyQuoteRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_DETAIL_RESPONSE_INVALID')
      const composition = parsed.data.organization_id
        ? await loadQuoteComposition(client, parsed.data.id)
        : { items: [] }
      return {
        quote: mapLegacyQuote(parsed.data),
        ...composition,
        meta: {
          source: 'supabase',
          retrievedAt: new Date().toISOString(),
          anomalies: !parsed.data.client && !parsed.data.lead ? 1 : 0,
        },
      }
    },
    async createDraft(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('create_quote_draft_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            ...(command.clientId ? { clientId: command.clientId } : {}),
            ...(command.leadId ? { leadId: command.leadId } : {}),
            ...(command.validUntil ? { validUntil: command.validUntil } : {}),
            ...(command.notes ? { notes: command.notes } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || `QUOTE_CREATE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
      const parsed = createDraftResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_CREATE_RESPONSE_INVALID')
      return parsed.data
    },
    async replaceDraftItems(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('replace_quote_items_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            quoteId: command.quoteId,
            expectedVersion: command.expectedVersion,
            items: command.items,
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `QUOTE_ITEMS_REPLACE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = replaceItemsResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_ITEMS_REPLACE_RESPONSE_INVALID')
      return parsed.data
    },
    async prepareDraft(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('prepare_quote_v1', { p_command: command }))
      if (response.error) {
        throw new Error(
          response.error.message || `QUOTE_PREPARATION_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = prepareQuoteResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_PREPARATION_RESPONSE_INVALID')
      return parsed.data
    },
    async sendPrepared(command) {
      // L'envoi n'est jamais exécuté par le navigateur : l'Edge Function réserve la
      // commande, produit le PDF, l'archive, appelle Resend puis finalise SENT.
      const invocation = await client.functions.invoke('send-quote', { body: command })
      if (invocation.error) throw new Error('QUOTE_DELIVERY_FAILED')
      const parsed = sendQuoteResultSchema.safeParse(invocation.data)
      if (!parsed.success) throw new Error('QUOTE_DELIVERY_RESPONSE_INVALID')
      return parsed.data
    },
    async acceptSent(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('accept_quote_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            quoteId: command.quoteId,
            expectedVersion: command.expectedVersion,
            acceptedAt: command.acceptedAt,
            source: command.source,
            ...(command.note ? { note: command.note } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `QUOTE_ACCEPTANCE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = acceptQuoteResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('QUOTE_ACCEPTANCE_RESPONSE_INVALID')
      return parsed.data
    },
    async setCompetitor(command) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = asResponse(
        await rpcClient.rpc('set_quote_competitor_v1', {
          p_command: { quoteId: command.quoteId, competitor: command.competitor },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'QUOTE_COMPETITOR_FAILED')
    },
  }
}
